/*
 * Copyright (C) 2026 to Present Applause App Quality, Inc. All rights reserved.
 */

import {
  AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Inject, Input, Output, signal,
} from '@angular/core';

export type AttachmentMode = 'image' | 'video';

export interface StatusAttachmentDraft {
  label: string;
  value: string;
  icon: string;
  // Set for files uploaded from this picker, so they can be deleted if replaced.
  id?: number;
}

const UPLOAD_ERRORS: Record<number, string> = {
  0: 'File too large',
  400: 'File type not allowed',
  413: 'File too large',
  429: 'You reached the maximum allowed uploads in an hour. Please try again later.',
};

let nextId = 0;

// Inline panel for attaching an image (upload or URL) or a video (URL) to a status.
@Component({
  selector: 'ut-status-attachment-picker',
  templateUrl: './status-attachment-picker.component.html',
  styleUrls: ['./status-attachment-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class StatusAttachmentPickerComponent implements AfterViewInit {
  @Input({ required: true }) mode: AttachmentMode;
  @Output() attached = new EventEmitter<StatusAttachmentDraft>();
  @Output() closed = new EventEmitter<void>();

  url = '';
  busy = signal(false);
  error = signal('');
  readonly titleId = `pickerTitle${nextId}`;
  readonly inputId = `pickerUrl${nextId}`;
  readonly errorId = `pickerError${nextId++}`;

  constructor(
    @Inject('UploadService') private UploadService: any,
    @Inject('EmbedlyService') private EmbedlyService: any,
    private host: ElementRef<HTMLElement>,
  ) {}

  // Opening the picker moves focus into it (the upload button for images, the URL field for videos).
  ngAfterViewInit(): void {
    this.host.nativeElement.querySelector('input')?.focus();
  }

  get title(): string {
    return this.mode === 'image' ? 'Insert an image' : 'Insert a video';
  }

  upload(input: HTMLInputElement): void {
    const file = input.files?.[0];
    if (!file) return;
    this.busy.set(true);
    this.UploadService.upload(file).then(({ data }) => {
      this.attached.emit({
        label: data.file_name, value: data.url, icon: 'insert_photo', id: data.id,
      });
    }, (response) => {
      this.error.set(UPLOAD_ERRORS[response.status] || 'File couldn\'t be uploaded');
    }).finally(() => {
      input.value = '';
      this.busy.set(false);
    });
  }

  insertUrl(): void {
    const url = this.url.trim();
    if (!url) return;
    this.busy.set(true);
    this.EmbedlyService.getEmbed(url).then(({ data }) => {
      if (!['image', 'video', 'photo'].includes(data.type)) {
        this.showUrlError(`Please provide a valid ${this.mode} URL`);
        return;
      }
      this.attached.emit(this.mode === 'image'
        ? { label: url.substring(url.lastIndexOf('/') + 1), value: url, icon: 'insert_photo' }
        : { label: data.title, value: url, icon: 'play_circle_filled' });
      this.url = '';
    }, (err) => {
      this.showUrlError(err.data?.errors?.[0]?.code || 'An unexpected error occurred. Please try a different URL.');
    }).finally(() => this.busy.set(false));
  }

  private showUrlError(message: string): void {
    this.error.set(message);
    this.host.nativeElement.querySelector<HTMLElement>(`#${this.inputId}`)?.focus();
  }
}
